import { Pressable, StyleSheet, Switch, Text, View } from "react-native";

import { webTapFloor, type UiTheme } from "../theme";

// THE settings toggle-row: a title (+ optional help subtitle) beside a native
// <Switch>. The idiom the Settings cards hand-rolled per file — HintsSettingsCard
// (×3), AudioSettingsCard (×1), and now the Health-Connect calorie preference —
// all re-rolling the same toggleRow/toggleText/rowTitle/rowSub StyleSheet + the
// {false:border,true:brand500} trackColor. One place so the row can't drift in
// spacing/weight/track colour between callsites (§CONSOLIDAMENTO).
//
// a11y: the WHOLE row is the tap target (a bare <Switch> is ~31pt tall, under
// the 44pt floor — settingsHealthConnectStyles.ts documents the same "hitSlop on
// the consumer" convention). The outer Pressable carries role=switch + the
// checked state; the Switch is display-only (its wrapper is pointer- and
// a11y-transparent) so a screen reader announces ONE switch node, not two, and a
// reduced-vision user can land anywhere on the tall row. State is never
// colour-alone — the thumb position + the announced checked state carry it.
export type ToggleRowProps = {
  theme: UiTheme;
  /** The switch's bold title. */
  label: string;
  /** Optional secondary help line under the title. */
  sublabel?: string;
  value: boolean;
  onValueChange: (next: boolean) => void;
  disabled?: boolean;
  /** Defaults to `label` when omitted. */
  accessibilityLabel?: string;
  /** Defaults to `sublabel`: the label above replaces the row's text for a
   *  screen reader, so without a hint the help line is never announced. */
  accessibilityHint?: string;
};

export function ToggleRow({
  theme,
  label,
  sublabel,
  value,
  onValueChange,
  disabled = false,
  accessibilityLabel,
  accessibilityHint,
}: ToggleRowProps) {
  const styles = makeStyles(theme);
  // Tipizzata a parte: i tipi di RN non dichiarano `activeThumbColor` (e' una
  // prop che react-native-web aggiunge), e uno spread evita l'excess-property
  // check che scatterebbe su un attributo JSX scritto a mano.
  const webActiveThumb: { activeThumbColor: string } = {
    activeThumbColor: theme.palette.inkOnBrand,
  };
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint ?? sublabel}
      accessibilityState={{ checked: value, disabled: disabled || undefined }}
      disabled={disabled}
      onPress={() => onValueChange(!value)}
      hitSlop={8}
      style={styles.row}
    >
      <View style={styles.text}>
        <Text style={styles.title}>{label}</Text>
        {sublabel ? <Text style={styles.sub}>{sublabel}</Text> : null}
      </View>
      {/* Display-only: the Pressable above drives the toggle + owns the a11y
          switch node; this View is pointer- and screen-reader-transparent. */}
      <View
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <Switch
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          trackColor={{
            false: theme.palette.border,
            true: theme.palette.brand500,
          }}
          // ⛔ Senza `thumbColor` il pallino NON e' tematizzato: Android lo
          // dipinge col `colorAccent` di AppCompat (styles.xml eredita
          // Theme.AppCompat.DayNight senza override), cioe' un teal di sistema
          // che non esce da themes.ts e stona in tutte e quattro le famiglie.
          // `ink3` sul track spento e `inkOnBrand` su quello acceso sono i due
          // ruoli che il tema gia' definisce per "si legge sopra questo fondo"
          // — nessun token nuovo. Misurato sulle quattro famiglie x due modi:
          // spento 2.94-5.46, acceso 4.60-10.45. Il minimo (catppuccin chiaro)
          // sfiora 3:1, e va bene perche' il pallino non porta lo STATO: lo
          // portano il colore del track e la posizione del pallino stesso.
          thumbColor={value ? theme.palette.inkOnBrand : theme.palette.ink3}
          // ⛔ E `thumbColor` da solo cura solo META' del difetto: su
          // react-native-web il pallino ACCESO non lo legge. Il suo Switch fa
          // `value ? (activeThumbColor ?? '#009688') : (thumbColor ?? ...)`
          // (dist/exports/Switch/index.js:80) ⇒ senza `activeThumbColor` il
          // web ricade su un default che e' **lo stesso teal** che stiamo
          // togliendo ad Android. RN nativo non conosce quella prop e la
          // ignora, quindi passarle entrambe e' l'unico modo di avere UN
          // comportamento su due piattaforme. ⚠️ Superficie reale, non
          // teorica: `_UnavailableHereDialog` monta due ToggleRow dentro il
          // Logger, che web-rnw serve da `WorkoutsScreen`.
          {...webActiveThumb}
        />
      </View>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s4,
      paddingVertical: s.s2,
      // hitSlop (below) bridges a short label to the 44pt floor on native;
      // react-native-web's Pressable ignores hitSlop entirely (DS-48), so
      // web needs the floor enforced directly. undefined on native, no-op.
      minHeight: webTapFloor,
    },
    text: { flex: 1, gap: 2 },
    title: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    sub: { color: p.ink3, fontSize: t.sizes.sm },
  });
}
